import React, { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowRight,
  ChevronRight,
  Lock,
  ShieldCheck,
  FileText,
  ClipboardList,
  Globe2,
  Eye,
  Briefcase,
  AlertTriangle,
  Database,
  Ban,
  Rocket,
  Radar,
  GitBranch,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import PilotSection from '../components/PilotSection';
import HussarTopNav from '../components/HussarTopNav';
import AIActSection from '../components/AIActSection';
import { track } from '../lib/analytics';

/**
 * HussarAi institutional homepage.
 *
 * The homepage `/` positions HussarAi as the immigration-record-integrity
 * platform. LeadWing (a separate small-business lead product) is
 * intentionally NOT marketed in the main body — it remains reachable via
 * a single small, isolated cross-link at the very bottom of the footer.
 * All LeadWing long-form copy lives on LeadWing's own routes.
 */
const Landing = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    document.title = t('brandPivot.metaTitle');
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute('content', t('brandPivot.metaDescription'));

    // v4.0 Phase 8 · Reset canonical to homepage after SPA route change.
    // Trust/demo pages set their own canonical on mount and restore this
    // homepage default on unmount; if a hard reload happens while React
    // rehydrates the homepage after a prior canonical override, force it.
    const c = document.head.querySelector('link[rel="canonical"]');
    if (c) c.setAttribute('href', 'https://hussarai.org/');
  }, [t]);

  const institutionalCards = [
    { key: 'case-hub',   icon: <GitBranch className="w-7 h-7" />,     to: '/moje-akta-imigracyjne',      titleKey: 'homeInst.caseHubCard.title',    bodyKey: 'homeInst.caseHubCard.body',    ctaKey: 'homeInst.caseHubCard.cta' },
    { key: 'compliance', icon: <FileText className="w-7 h-7" />,      to: '/dla-instytucji',              titleKey: 'homeInst.complianceCard.title', bodyKey: 'homeInst.complianceCard.body', ctaKey: 'homeInst.complianceCard.cta' },
    { key: 'integrity',  icon: <AlertTriangle className="w-7 h-7" />, to: '/bezpieczenstwo-i-zgodnosc',   titleKey: 'homeInst.integrityCard.title',  bodyKey: 'homeInst.integrityCard.body',  ctaKey: 'homeInst.integrityCard.cta' },
    { key: 'intel',      icon: <Radar className="w-7 h-7" />,         to: '/operator-zgodnosci',          titleKey: 'homeInst.intelCard.title',      bodyKey: 'homeInst.intelCard.body',      ctaKey: 'homeInst.intelCard.cta' },
    { key: 'admin',      icon: <Briefcase className="w-7 h-7" />,     to: '/dashboard-akt-imigracyjnych', titleKey: 'homeInst.adminCard.title',      bodyKey: 'homeInst.adminCard.body',      ctaKey: 'homeInst.adminCard.cta' },
    { key: 'no-auto',    icon: <Ban className="w-7 h-7" />,           to: '/bezpieczenstwo-i-zgodnosc',   titleKey: 'homeInst.noAutoCard.title',     bodyKey: 'homeInst.noAutoCard.body',     ctaKey: 'homeInst.noAutoCard.cta' },
    { key: 'data-gov',   icon: <Database className="w-7 h-7" />,      to: '/protokol-ai',                 titleKey: 'homeInst.dataGovCard.title',    bodyKey: 'homeInst.dataGovCard.body',    ctaKey: 'homeInst.dataGovCard.cta' },
    { key: 'pilot',      icon: <Rocket className="w-7 h-7" />,        to: '/pilot-publiczny',             titleKey: 'homeInst.pilotCard.title',      bodyKey: 'homeInst.pilotCard.body',      ctaKey: 'homeInst.pilotCard.cta' },
  ];

  return (
    <div className="hussar-landing">
      <HussarTopNav />

      <section className="hussar-hero">
        <div className="hussar-container">
          <div className="hussar-hero-content">
            {/* Eyebrow — single-promise positioning anchor */}
            <p
              className="hussar-hero-eyebrow"
              data-testid="hero-eyebrow"
              style={{
                fontSize: 11, letterSpacing: 1.6, fontWeight: 700,
                color: '#7DD3FC', marginBottom: 12, textTransform: 'uppercase',
              }}
            >
              {t('brandPivot.heroEyebrow')}
            </p>
            <h1 className="hussar-hero-title" data-testid="hero-h1">{t('brandPivot.heroTitle')}</h1>
            <p className="hussar-hero-subtitle" data-testid="hero-subtitle">
              {t('brandPivot.heroSubtitle')}
            </p>
            <p className="hussar-hero-secondary" data-testid="hero-trust-line">
              {t('brandPivot.trustLine')}
            </p>
            {/* Safety line — v4.0 doctrine lockdown. Renders the explicit
                "HussarAi does not decide immigration status and does not
                judge people" statement above the CTA row so the boundary
                is legible before any action. */}
            <p
              data-testid="hero-safety-line"
              style={{
                fontSize: 13, lineHeight: 1.55, fontWeight: 500,
                color: 'rgba(226,232,240,0.78)',
                marginTop: 14, maxWidth: 640,
              }}
            >
              {t('brandPivot.safetyLineHero')}
            </p>
            {/* European AI governance era strip — implicit framing, no
                regulator name-drop. */}
            <p
              data-testid="hero-european-era"
              style={{
                fontSize: 12, letterSpacing: 0.4, fontWeight: 600,
                color: 'rgba(125,211,252,0.85)', marginTop: 12,
              }}
            >
              {t('brandPivot.heroEuropeanEra')}
            </p>
            <div className="hussar-hero-actions" data-testid="hero-twin-ctas">
              {/* People-first CTA → /moje-akta-imigracyjne */}
              <Button
                onClick={() => {
                  try { track('homepage_hero_cta_clicked', { target: 'people' }); } catch (_) { /* noop */ }
                  navigate('/moje-akta-imigracyjne');
                }}
                className="hussar-btn-primary hussar-btn-large"
                data-testid="hero-cta-people"
              >
                {t('brandPivot.heroCtaPeople')}
                <ArrowRight className="w-5 h-5" />
              </Button>
              {/* Institutions CTA → /operator-zgodnosci */}
              <Button
                onClick={() => {
                  try { track('homepage_hero_cta_clicked', { target: 'institutions' }); } catch (_) { /* noop */ }
                  navigate('/operator-zgodnosci');
                }}
                className="hussar-btn-secondary hussar-btn-large"
                data-testid="hero-cta-institutions"
              >
                {t('brandPivot.heroCtaInstitutions')}
                <ChevronRight className="w-5 h-5" />
              </Button>
              {/* Review-demo CTA (v4.0 Phase 6) → dedicated demo route. */}
              <Button
                onClick={() => {
                  try { track('homepage_hero_cta_clicked', { target: 'review_demo' }); } catch (_) { /* noop */ }
                  navigate('/review-akt-imigracyjnych');
                }}
                className="hussar-btn-secondary hussar-btn-large"
                data-testid="hero-cta-review-demo"
              >
                {t('brandPivot.heroCtaDemo')}
                <ChevronRight className="w-5 h-5" />
              </Button>
            </div>
            {/* Subtle tertiary text link to the knowledge index — visible
                but not competing with the two primary CTAs above. */}
            <Link
              to="/wiedza"
              data-testid="hero-wiedza-link"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                marginTop: 14, fontSize: 13, fontWeight: 500,
                color: 'rgba(125,211,252,0.85)', textDecoration: 'none',
              }}
            >
              {t('wiedzaLink.homepageLabel')} →
            </Link>
            {/* Neutral trust pledge — replaced the donation-affiliated
                hero badge to keep HussarAi free of any implied government
                or political affiliation. Read-only, no navigation. */}
            <p
              className="hussar-honor-badge hussar-honor-badge-subtle"
              data-testid="hero-trust-pledge"
              style={{ cursor: 'default' }}
            >
              <ShieldCheck className="w-4 h-4" aria-hidden="true" />
              <span>{t('homeInst.trustPledge')}</span>
            </p>
          </div>
        </div>
      </section>

      {/* Hussar/Vienna brand spine — institutional, protective, dignified.
          Three lines: brand wordmark · tagline · support. Reads as
          procurement-safe positioning ("AI that protects European
          organisations"), not a battle slogan. */}
      <section
        className="hussar-spine"
        data-testid="brand-spine"
        aria-label={t('homeInst.spine.brand')}
      >
        <div className="hussar-container">
          <div className="hussar-spine-inner">
            <p className="hussar-spine-brand" data-testid="brand-spine-brand">
              {t('homeInst.spine.brand')}
            </p>
            <p className="hussar-spine-tagline" data-testid="brand-spine-tagline">
              {t('homeInst.spine.tagline')}
            </p>
            <p className="hussar-spine-support" data-testid="brand-spine-support">
              {t('homeInst.spine.support')}
            </p>
          </div>
        </div>
      </section>

      {/* Protector framing — sits between the brand spine and the pillar
          grid. Two lines only (title + body): explains the brand myth
          (protection, not automation of responsibility) in dignified,
          procurement-safe language. No icons, no CTAs — pure copy block
          so it never competes with the hero CTAs above. */}
      <section
        className="hussar-protector"
        data-testid="brand-protector"
        aria-label={t('brandPivot.protectionTitle')}
      >
        <div className="hussar-container">
          <h2 className="hussar-protector-title" data-testid="brand-protector-title">
            {t('brandPivot.protectionTitle')}
          </h2>
          <p className="hussar-protector-body" data-testid="brand-protector-body">
            {t('brandPivot.protectionBody')}
          </p>
        </div>
      </section>

      {/* Three-layers framing — people-first reframing of the
          institutional backbone. Sits between the protector copy and the
          existing institutional pillar grid. No CTAs, just identity copy
          for the three audiences: people, helpers, institutions. */}
      <section
        className="hussar-section"
        data-testid="three-layers"
        aria-label={t('brandPivot.threeLayers.heading')}
        style={{ paddingTop: 24, paddingBottom: 24 }}
      >
        <div className="hussar-container">
          <p
            className="hussar-section-eyebrow"
            data-testid="three-layers-eyebrow"
          >
            {t('brandPivot.threeLayers.eyebrow')}
          </p>
          <h2
            className="hussar-section-title"
            data-testid="three-layers-heading"
          >
            {t('brandPivot.threeLayers.heading')}
          </h2>
          <p
            className="hussar-section-subtitle"
            data-testid="three-layers-subheading"
          >
            {t('brandPivot.threeLayers.subheading')}
          </p>

          <div
            data-testid="three-layers-grid"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: 16,
              marginTop: 18,
            }}
          >
            {['people', 'helpers', 'institutions'].map((key) => (
              <Card
                key={key}
                data-testid={`three-layers-card-${key}`}
                style={{
                  padding: '20px 18px',
                  background: 'rgba(15,23,42,0.45)',
                  border: '1px solid rgba(56,189,248,0.18)',
                  borderRadius: 12,
                }}
              >
                <h3
                  data-testid={`three-layers-card-${key}-title`}
                  style={{ fontSize: 16, fontWeight: 700, margin: 0, color: '#E2E8F0' }}
                >
                  {t(`brandPivot.threeLayers.${key}.title`)}
                </h3>
                <p
                  data-testid={`three-layers-card-${key}-headline`}
                  style={{
                    fontSize: 15, lineHeight: 1.45, marginTop: 8,
                    fontWeight: 600, color: '#7DD3FC',
                  }}
                >
                  {t(`brandPivot.threeLayers.${key}.headline`)}
                </p>
                <p
                  data-testid={`three-layers-card-${key}-body`}
                  style={{
                    fontSize: 13, lineHeight: 1.55, marginTop: 10,
                    color: 'rgba(226,232,240,0.78)',
                  }}
                >
                  {t(`brandPivot.threeLayers.${key}.body`)}
                </p>
                <p
                  data-testid={`three-layers-card-${key}-audience`}
                  style={{
                    fontSize: 11, marginTop: 12,
                    color: '#38BDF8', letterSpacing: 0.3,
                  }}
                >
                  {t(`brandPivot.threeLayers.${key}.audience`)}
                </p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Safety strip — explicit "what HussarAi does NOT do" boundary.
          Sits below the three-layers section so visitors who scrolled past
          the hero still encounter the safety language before any CTA. */}
      <section
        className="hussar-section"
        data-testid="brand-safety"
        aria-label={t('brandPivot.safety.title')}
        style={{ paddingTop: 8, paddingBottom: 32 }}
      >
        <div className="hussar-container">
          <Card
            data-testid="brand-safety-card"
            style={{
              padding: '18px 18px',
              background: 'rgba(248,113,113,0.06)',
              border: '1px solid rgba(248,113,113,0.32)',
              borderRadius: 12,
            }}
          >
            <h3
              data-testid="brand-safety-title"
              style={{ fontSize: 15, fontWeight: 700, margin: 0, color: '#F87171' }}
            >
              {t('brandPivot.safety.title')}
            </h3>
            <p
              data-testid="brand-safety-body"
              style={{
                fontSize: 13, lineHeight: 1.55, marginTop: 8,
                color: 'rgba(226,232,240,0.82)',
              }}
            >
              {t('brandPivot.safety.body')}
            </p>
          </Card>
        </div>
      </section>

      {/* Record-vs-status clarifier — explicit "we verify the record,
          not the status" boundary. Sits immediately below the safety
          strip so visitors encounter the people-first / record-not-status
          framing in the same scan as the doctrine. */}
      <section
        className="hussar-section"
        data-testid="record-vs-status"
        aria-label={t('brandPivot.recordVsStatus.title')}
        style={{ paddingTop: 0, paddingBottom: 32 }}
      >
        <div className="hussar-container">
          <Card
            data-testid="record-vs-status-card"
            style={{
              padding: '18px 18px',
              background: 'rgba(56,189,248,0.06)',
              border: '1px solid rgba(56,189,248,0.32)',
              borderRadius: 12,
            }}
          >
            <h3
              data-testid="record-vs-status-title"
              style={{ fontSize: 15, fontWeight: 700, margin: 0, color: '#38BDF8' }}
            >
              {t('brandPivot.recordVsStatus.title')}
            </h3>
            <p
              data-testid="record-vs-status-body"
              style={{
                fontSize: 13, lineHeight: 1.55, marginTop: 8,
                color: 'rgba(226,232,240,0.82)',
              }}
            >
              {t('brandPivot.recordVsStatus.body')}
            </p>
          </Card>
        </div>
      </section>

      {/* Institutional sections — compliance, administration, integrity,
          data governance, no-auto-decisions, pilot. Each is a single card
          with a CTA pointing to the dedicated page. No LeadWing copy. */}
      <section
        id="sekcje"
        className="hussar-section hussar-inst-section"
        data-testid="inst-sections"
      >
        <div className="hussar-container">
          <p className="hussar-section-eyebrow" data-testid="inst-eyebrow">
            {t('homeInst.eyebrow')}
          </p>
          <h2 className="hussar-section-title" data-testid="inst-sections-title">
            {t('homeInst.sectionsHeading')}
          </h2>
          <p className="hussar-section-subtitle" data-testid="inst-sections-subtitle">
            {t('homeInst.sectionsSubheading')}
          </p>

          <div className="hussar-inst-grid" data-testid="inst-grid">
            {institutionalCards.map((card) => (
              <Card
                key={card.key}
                className="hussar-inst-card"
                data-testid={`inst-card-${card.key}`}
              >
                <div className="hussar-inst-card-icon" aria-hidden="true">
                  {card.icon}
                </div>
                <h3
                  className="hussar-inst-card-title"
                  data-testid={`inst-card-${card.key}-title`}
                >
                  {t(card.titleKey)}
                </h3>
                <p
                  className="hussar-inst-card-body"
                  data-testid={`inst-card-${card.key}-body`}
                >
                  {t(card.bodyKey)}
                </p>
                <Link
                  to={card.to}
                  className="hussar-inst-card-cta"
                  data-testid={`inst-card-${card.key}-cta`}
                  onClick={() => {
                    try {
                      track('homepage_inst_card_clicked', { card: card.key });
                    } catch (_) { /* noop */ }
                  }}
                >
                  {t(card.ctaKey)}
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </Card>
            ))}
          </div>

          {/* Subtle institutional trust badge — sits below the cards. Signals
              "AI support, not automated decision-making" to procurement,
              administration and compliance buyers. */}
          <div
            className="hussar-inst-trust-badge"
            data-testid="inst-trust-badge"
            role="note"
            aria-label={t('homeInst.trustBadge')}
          >
            <ShieldCheck className="w-3.5 h-3.5" aria-hidden="true" />
            <span>{t('homeInst.trustBadge')}</span>
          </div>
        </div>
      </section>

      {/* AI Act doctrinal section — 5 cards explaining how HussarAi fits
          the AI Act-era governance pattern. Pure institutional content,
          no automated-decision claims, no guaranteed-compliance claims. */}
      <AIActSection variant="home" ctaHref="/ai-act-polska" />

      {/* Pilot signup section — keeps the existing controlled-pilot form. */}
      <PilotSection />

      {/* Trust block — Polish-first, AI transparency, Honor pledge. */}
      <section className="hussar-trust-section" data-testid="trust-section">
        <div className="hussar-container">
          <h2 className="hussar-section-title">{t('trust.heading')}</h2>
          <div className="hussar-trust-grid">
            <Card className="hussar-trust-card">
              <Globe2 className="hussar-trust-icon" />
              <h3 className="hussar-trust-title">{t('trust.polishTitle')}</h3>
              <p className="hussar-trust-text">{t('trust.polishText')}</p>
              <button
                type="button"
                className="hussar-trust-link"
                onClick={() => navigate('/bezpieczenstwo-i-zgodnosc')}
                data-testid="trust-polish-link"
              >
                {t('trust.polishLink')} →
              </button>
            </Card>
            <Card className="hussar-trust-card">
              <Eye className="hussar-trust-icon" />
              <h3 className="hussar-trust-title">{t('trust.transparencyTitle')}</h3>
              <p className="hussar-trust-text">{t('trust.transparencyText')}</p>
              <button
                type="button"
                className="hussar-trust-link"
                onClick={() => navigate('/odpowiedzialnosc')}
                data-testid="trust-transparency-link"
              >
                {t('trust.transparencyLink')} →
              </button>
            </Card>
            <Card className="hussar-trust-card">
              <ShieldCheck className="hussar-trust-icon hussar-trust-icon-crimson" />
              <h3 className="hussar-trust-title">{t('trust.honorTitle')}</h3>
              <p className="hussar-trust-text">{t('trust.honorText')}</p>
              <button
                type="button"
                className="hussar-trust-link"
                onClick={() => navigate('/bezpieczenstwo-i-zgodnosc')}
                data-testid="trust-honor-link"
              >
                {t('trust.honorLink')} →
              </button>
            </Card>
          </div>
        </div>
      </section>

      <section className="hussar-section hussar-cta-section">
        <div className="hussar-container">
          <div className="hussar-cta-content">
            <h2 className="hussar-cta-title" data-testid="final-cta-title">
              {t('homeInst.finalTitle')}
            </h2>
            <p className="hussar-cta-subtitle" data-testid="final-cta-subtitle">
              {t('homeInst.finalSubtitle')}
            </p>
            <Button
              onClick={() => navigate('/operator-zgodnosci')}
              className="hussar-btn-primary hussar-btn-large"
              data-testid="final-cta-btn"
            >
              {t('homeInst.finalCta')}
              <ArrowRight className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </section>

      <footer className="hussar-footer">
        <div className="hussar-container">
          <div className="hussar-footer-content">
            <div className="hussar-footer-brand">
              <div className="hussar-logo">
                <img src="/hussarai-logo.png" alt="HussarAi" className="hussar-logo-img-footer" />
                <span className="hussar-logo-text">HussarAi</span>
              </div>
              <p className="hussar-footer-tagline">{t('footer.tagline')}</p>
            </div>
            <div className="hussar-footer-links">
              <div className="hussar-footer-section">
                <h4>{t('footer.product')}</h4>
                <a href="/moje-akta-imigracyjne">{t('hussarNav.mojeAkta')}</a>
                <a href="/dla-profesjonalistow">{t('hussarNav.forProfessionals')}</a>
                <a href="/dla-instytucji">{t('hussarNav.forInstitutions')}</a>
                <a href="/bezpieczenstwo-i-zgodnosc">{t('hussarNav.security')}</a>
                <a
                  href="/bezpieczenstwo-i-zgodnosc"
                  data-testid="footer-security-architecture-link"
                >
                  {t('footer.securityArchitecture')}
                </a>
                <a href="/pilot-publiczny">{t('hussarNav.pilot')}</a>
              </div>
              <div className="hussar-footer-section">
                <h4>{t('footer.company')}</h4>
                <a href="/odpowiedzialnosc" data-testid="footer-responsibility-link">{t('responsibility.pageTitle')}</a>
                <a href="/press-kit" data-testid="footer-press-kit-link">{t('footer.pressKit')}</a>
                <a href="mailto:audyt@hussarai.org" data-testid="footer-contact-link">{t('hussarNav.contact')}</a>
              </div>
            </div>
          </div>
          <div className="hussar-footer-bottom">
            <p>{t('footer.rights')}</p>
            <p className="hussar-footer-origin">
              {t('footer.madeIn')} <span aria-hidden="true">🇵🇱</span>
            </p>
            {/* Quiet founder attribution — moved out of hero (2026-02-19).
                Single canonical link to /konrad-kwiatkowski entity page. */}
            <p
              className="hussar-footer-founder"
              data-testid="footer-founder-reference"
              style={{
                fontSize: 12, fontWeight: 400,
                color: 'rgba(203,213,245,0.55)', margin: '4px 0 0',
              }}
            >
              {t('brandPivot.footerFounderLabel')}:{' '}
              <Link
                to="/konrad-kwiatkowski"
                data-testid="footer-founder-link"
                style={{ color: 'rgba(125,211,252,0.85)', textDecoration: 'underline' }}
              >
                Konrad Kwiatkowski
              </Link>
            </p>
          </div>
          <p className="hussar-footer-ai-disclosure" data-testid="footer-ai-disclosure">
            {t('footer.aiDisclosure')}
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
